import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Circle, Clock, Lock, Calendar, Radio, FileText, Upload, Download } from "lucide-react";
import { motion } from "framer-motion";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const STATUS_OPTS = [
  { v: "pending", l: "Pendiente", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
  { v: "in_progress", l: "En curso", cls: "bg-amber-500/15 text-amber-500 border-amber-500/30" },
  { v: "delivered", l: "Entregada", cls: "bg-blue-500/15 text-blue-500 border-blue-500/30" },
  { v: "completed", l: "Completada", cls: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30" },
];

export default function ProducerPortal() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const [pin, setPin] = useState(sp.get("pin") || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [authed, setAuthed] = useState(!!sp.get("pin"));
  const [activeOrder, setActiveOrder] = useState(null);

  const load = useCallback(async () => {
    setError(""); setBusy(true);
    try {
      const r = await fetch(`${BACKEND}/api/produccion/${slug}?pin=${encodeURIComponent(pin)}`);
      if (r.status === 403) { setError("PIN inválido. Intentá nuevamente."); setAuthed(false); setData(null); return; }
      if (r.status === 404) { setError("Programa no encontrado."); return; }
      if (!r.ok) throw new Error("Error de carga");
      const j = await r.json();
      setData(j); setAuthed(true);
    } catch (e) { setError(e.message || "Error"); }
    finally { setBusy(false); }
  }, [slug, pin]);

  useEffect(() => { if (sp.get("pin")) load(); }, [load, sp]);

  useEffect(() => {
    const onRefresh = () => load();
    window.addEventListener("portal-refresh", onRefresh);
    return () => window.removeEventListener("portal-refresh", onRefresh);
  }, [load]);

  const updateAction = async (orderId, actionId, patch) => {
    try {
      const r = await fetch(`${BACKEND}/api/produccion/${slug}/orders/${orderId}/actions/${actionId}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin, ...patch }),
      });
      if (!r.ok) { alert("Error al actualizar"); return; }
      await load();
      // Refresh activeOrder if open
      if (activeOrder) {
        const fresh = (await fetch(`${BACKEND}/api/produccion/${slug}?pin=${encodeURIComponent(pin)}`).then((x) => x.json())).orders.find((o) => o.id === activeOrder.id);
        setActiveOrder(fresh || null);
      }
    } catch (e) { alert("Error de red"); }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <motion.form onSubmit={(e) => { e.preventDefault(); load(); }} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md border border-border rounded-sm p-8 bg-secondary/10" data-testid="producer-portal-pin-form">
          <Radio className="w-10 h-10 text-primary mb-4" />
          <h1 className="font-display font-black text-3xl mb-2 tracking-tighter">Portal Productor</h1>
          <p className="text-sm text-muted-foreground mb-6">Ingresá el PIN del programa para acceder a tus órdenes de ejecución.</p>
          <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">PIN del programa</label>
          <input value={pin} onChange={(e) => setPin(e.target.value)} placeholder="123456" className="w-full h-11 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none mb-3" data-testid="producer-pin-input" autoFocus />
          {error && <p className="text-sm text-destructive mb-3">{error}</p>}
          <button type="submit" disabled={busy || !pin} data-testid="producer-pin-submit" className="w-full h-11 bg-primary text-primary-foreground rounded-sm text-sm font-semibold disabled:opacity-50">
            {busy ? "Verificando..." : "Ingresar"}
          </button>
          <p className="text-[10px] text-muted-foreground mt-4 italic flex items-center gap-1"><Lock className="w-3 h-3" /> Acceso seguro · sin información comercial ni montos.</p>
        </motion.form>
      </div>
    );
  }

  const program = data?.program || {};
  const orders = data?.orders || [];

  return (
    <div className="min-h-screen bg-background" data-testid="producer-portal">
      <header className="border-b border-border bg-secondary/20">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            {program.logo && <img src={program.logo.startsWith("/") ? `${BACKEND}${program.logo}` : program.logo} alt={program.name} className="w-14 h-14 object-contain rounded-sm" />}
            <div>
              <p className="text-[10px] uppercase tracking-widest text-primary font-bold">Portal Productor · INFLUCAST</p>
              <h1 className="font-display text-3xl font-black tracking-tighter">{program.name}</h1>
              <p className="text-xs text-muted-foreground">{program.schedule || ""}</p>
            </div>
          </div>
          <button onClick={load} className="text-xs uppercase tracking-widest font-semibold text-muted-foreground hover:text-foreground border border-border px-3 py-2 rounded-sm">Actualizar</button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-xl">Órdenes de ejecución ({orders.length})</h2>
          <p className="text-xs text-muted-foreground italic flex items-center gap-1"><Lock className="w-3 h-3" /> Sin información financiera</p>
        </div>

        {orders.length === 0 ? (
          <p className="text-sm text-muted-foreground border border-dashed border-border rounded-sm p-8 text-center">Sin órdenes asignadas todavía.</p>
        ) : (
          <div className="grid lg:grid-cols-2 gap-4">
            {orders.map((o) => {
              const acts = o.actions || [];
              const done = acts.filter((a) => a.status === "completed" || a.status === "delivered").length;
              const pct = acts.length ? Math.round(done * 100 / acts.length) : 0;
              return (
                <motion.button
                  key={o.id}
                  onClick={() => setActiveOrder(o)}
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  className="text-left border border-border hover:border-primary rounded-sm p-5 bg-secondary/10 transition-colors"
                  data-testid={`order-card-${o.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{o.code}</p>
                      <h3 className="font-display font-bold text-lg">{o.title || "Orden"}</h3>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm bg-secondary">{o.status || "draft"}</span>
                  </div>
                  {o.client_visible_name && <p className="text-xs text-muted-foreground mb-3">Cliente visible: <span className="font-semibold text-foreground">{o.client_visible_name}</span></p>}
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                    {o.start_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {o.start_date}</span>}
                    {o.end_date && <span>→ {o.end_date}</span>}
                  </div>
                  <div className="h-1.5 bg-secondary rounded-full overflow-hidden mb-1">
                    <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-[10px] text-muted-foreground">{done} / {acts.length} acciones · {pct}%</p>
                </motion.button>
              );
            })}
          </div>
        )}
      </main>

      {activeOrder && (
        <OrderPanel order={activeOrder} onClose={() => setActiveOrder(null)} onAction={(actionId, patch) => updateAction(activeOrder.id, actionId, patch)} program={program} />
      )}
    </div>
  );
}

function OrderPanel({ order, onClose, onAction, program }) {
  const downloadPdf = async () => {
    // public route is /api/admin/orders/:id/pdf — but admin only. Use produccion view (no PDF endpoint public yet).
    alert("Descarga de PDF no disponible desde el portal público. Pedí el PDF a tu contacto comercial INFLUCAST.");
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-end p-3" onClick={onClose}>
      <motion.div initial={{ x: 60, opacity: 0 }} animate={{ x: 0, opacity: 1 }} className="bg-background w-full max-w-2xl h-full overflow-y-auto rounded-sm border border-border" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-background z-10">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{order.code}</p>
            <h2 className="font-display font-black text-2xl tracking-tighter">{order.title}</h2>
          </div>
          <div className="flex gap-2">
            <button onClick={downloadPdf} className="px-3 py-2 border border-border rounded-sm text-xs font-semibold hover:border-primary inline-flex items-center gap-1"><Download className="w-3 h-3" /> PDF</button>
            <button onClick={onClose} className="text-2xl leading-none">×</button>
          </div>
        </div>
        <div className="p-5 space-y-5">
          {order.brief && (
            <section>
              <h3 className="font-display font-bold mb-2 flex items-center gap-2"><FileText className="w-4 h-4" /> Brief</h3>
              <p className="text-sm whitespace-pre-wrap text-muted-foreground bg-secondary/30 p-3 rounded-sm">{order.brief}</p>
            </section>
          )}
          {(order.materials_links || []).length > 0 && (
            <section>
              <h3 className="font-display font-bold mb-2">Materiales</h3>
              <ul className="space-y-1">
                {order.materials_links.map((l, i) => (
                  <li key={i}><a href={l} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline break-all">{l}</a></li>
                ))}
              </ul>
            </section>
          )}
          <section>
            <h3 className="font-display font-bold mb-3">Acciones de ejecución ({(order.actions || []).length})</h3>
            <div className="space-y-3">
              {(order.actions || []).map((a, i) => <ActionRow key={a.id || i} action={{ ...a, _order_id: order.id }} index={i + 1} onUpdate={(patch) => onAction(a.id, patch)} />)}
              {(order.actions || []).length === 0 && <p className="text-xs italic text-muted-foreground">Sin acciones cargadas.</p>}
            </div>
          </section>
        </div>
      </motion.div>
    </div>
  );
}

function ActionRow({ action, index, onUpdate }) {
  const [obs, setObs] = useState(action.observations_production || "");
  const [evidenceUrl, setEvidenceUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const cls = STATUS_OPTS.find((s) => s.v === action.status)?.cls || STATUS_OPTS[0].cls;
  const isDone = action.status === "completed" || action.status === "delivered";

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { alert("Archivo > 10MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("pin", new URL(window.location.href).searchParams.get("pin") || "");
      fd.append("order_id", action._order_id || "");
      fd.append("action_id", action.id);
      fd.append("file", f);
      const slug = window.location.pathname.split("/produccion/")[1]?.split("/")[0];
      const r = await fetch(`${BACKEND}/api/produccion/${slug}/upload`, { method: "POST", body: fd });
      if (!r.ok) { alert("Error al subir"); return; }
      // refresh outer list
      window.dispatchEvent(new CustomEvent("portal-refresh"));
    } catch (err) { alert("Error de red"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div className={`border rounded-sm p-3 ${isDone ? "border-emerald-500/30 bg-emerald-500/5" : "border-border bg-secondary/10"}`} data-testid={`action-row-${action.id}`}>
      <div className="flex items-start gap-3 mb-2">
        <div className="mt-0.5">{isDone ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Circle className="w-5 h-5 text-muted-foreground" />}</div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">{index}. {action.type || "Acción"}</p>
          {action.description && <p className="text-xs text-muted-foreground">{action.description}</p>}
          <div className="flex flex-wrap gap-2 mt-2 text-[10px] text-muted-foreground">
            {action.scheduled_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {action.scheduled_date}</span>}
            {action.duration && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {action.duration}</span>}
          </div>
        </div>
        <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm border ${cls}`}>{action.status || "pending"}</span>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        {STATUS_OPTS.map((s) => (
          <button key={s.v} onClick={() => onUpdate({ status: s.v })} data-testid={`status-${action.id}-${s.v}`} className={`text-xs font-semibold py-1.5 rounded-sm border ${action.status === s.v ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>{s.l}</button>
        ))}
      </div>
      <details className="text-xs">
        <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Notas y evidencia</summary>
        <div className="mt-2 space-y-2">
          <textarea value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Observaciones de producción..." rows={2} className="w-full px-2 py-1.5 bg-background border border-border rounded-sm text-xs resize-none" />
          <button onClick={() => onUpdate({ observations_production: obs })} className="px-3 py-1.5 border border-border rounded-sm text-[10px] font-semibold hover:border-primary">Guardar nota</button>
          <div className="pt-2 border-t border-border space-y-2">
            <label className="flex items-center justify-center gap-2 px-3 h-9 border border-dashed border-primary text-primary rounded-sm text-[10px] font-semibold cursor-pointer hover:bg-primary/5" data-testid={`upload-${action.id}`}>
              <Upload className="w-3 h-3" /> {uploading ? "Subiendo..." : "Subir archivo (foto/video/PDF · máx 10MB)"}
              <input type="file" accept="image/*,video/*,application/pdf" onChange={handleUpload} disabled={uploading} className="hidden" />
            </label>
            <div className="flex gap-2">
              <input value={evidenceUrl} onChange={(e) => setEvidenceUrl(e.target.value)} placeholder="o pegá una URL (post, story, drive…)" className="flex-1 h-8 px-2 bg-background border border-border rounded-sm text-xs" />
              <button onClick={() => { if (evidenceUrl) { onUpdate({ evidence: { url: evidenceUrl, type: "link" } }); setEvidenceUrl(""); } }} className="px-3 h-8 bg-primary text-primary-foreground rounded-sm text-[10px] font-semibold inline-flex items-center gap-1"><Upload className="w-3 h-3" /> URL</button>
            </div>
          </div>
          {(action.evidences || []).length > 0 && (
            <ul className="space-y-1 pt-2">
              {action.evidences.map((ev, ei) => (
                <li key={ei} className="text-[10px] text-muted-foreground flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  <a href={ev.url.startsWith("/") ? `${BACKEND}${ev.url}` : ev.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate">{ev.filename || ev.url}</a>
                  <span className="text-muted-foreground">{(ev.uploaded_at || "").slice(0, 10)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </details>
    </div>
  );
}
